import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BRANCH_TYPES, STATUS_OPTIONS } from "@/lib/constants";
import { Plus, Pencil, Trash2, Building2, Star } from "lucide-react";

const EMPTY = {
  name: "",
  branch_type: "workshop",
  code: "",
  manager_name: "",
  phone: "",
  email: "",
  address: "",
  is_main: false,
  status: "active",
};

export default function Sucursales() {
  const { isSuperAdmin, activeTenantId, refreshTenant } = useTenant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const canQuery = !isSuperAdmin || !!activeTenantId;

  async function fetchList() {
    if (!canQuery) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/branches", { params: tenantParam });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId, isSuperAdmin]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setSheetOpen(true);
  }
  function openEdit(b) {
    setEditing(b);
    setForm({ ...EMPTY, ...b });
    setSheetOpen(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/branches/${editing.id}`, form);
        toast.success("Sucursal actualizada");
      } else {
        await api.post("/branches", form, { params: tenantParam });
        toast.success("Sucursal creada");
      }
      setSheetOpen(false);
      fetchList();
      refreshTenant();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      await api.delete(`/branches/${confirmDelete.id}`);
      toast.success("Sucursal eliminada");
      setConfirmDelete(null);
      fetchList();
      refreshTenant();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6" data-testid="page-sucursales">
      <PageHeader
        title="Sucursales"
        description="Cada tenant debe tener al menos una sucursal principal. Los usuarios pertenecen a una sucursal."
        actions={
          <Button className="btn-tenant" onClick={openCreate} disabled={!canQuery} data-testid="btn-new-sucursal">
            <Plus className="h-4 w-4 mr-2" /> Nueva sucursal
          </Button>
        }
      />

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2].map((i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={canQuery ? "Aún no hay sucursales" : "Selecciona un tenant"}
            description="Registra la sucursal principal para poder dar de alta usuarios y vehículos."
            icon={Building2}
            action={
              canQuery && (
                <Button className="btn-tenant" onClick={openCreate} data-testid="empty-new-sucursal">
                  <Plus className="h-4 w-4 mr-2" /> Crear sucursal
                </Button>
              )
            }
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Encargado</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Estatus</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((b) => (
                <TableRow key={b.id} className="tbl-row" data-testid={`row-sucursal-${b.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{b.name}</span>
                      {b.is_main && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1">
                          <Star className="h-3 w-3" /> Principal
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mono">{b.code || ""}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {BRANCH_TYPES.find((t) => t.value === b.branch_type)?.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{b.manager_name || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <div>{b.phone || "—"}</div>
                    <div className="text-xs text-muted-foreground">{b.email || ""}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={b.status === "active" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                      {b.status === "active" ? "Activa" : "Inactiva"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`btn-edit-sucursal-${b.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!b.is_main && (
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(b)} data-testid={`btn-delete-sucursal-${b.id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="sheet-sucursal">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar sucursal" : "Nueva sucursal"}</SheetTitle>
            <SheetDescription>Solo puede haber una sucursal principal por tenant.</SheetDescription>
          </SheetHeader>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sucursal-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.branch_type} onValueChange={(v) => setForm({ ...form, branch_type: v })}>
                  <SelectTrigger data-testid="sucursal-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BRANCH_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Código</Label>
                <Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="sucursal-code" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Encargado</Label>
              <Input value={form.manager_name || ""} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} data-testid="sucursal-manager" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sucursal-phone" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sucursal-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="sucursal-address" />
            </div>
            <div className="flex items-center justify-between pt-2">
              <div>
                <Label htmlFor="is_main">Sucursal principal</Label>
                <p className="text-xs text-muted-foreground">Solo puede haber una por tenant.</p>
              </div>
              <Switch
                id="is_main"
                checked={form.is_main}
                onCheckedChange={(v) => setForm({ ...form, is_main: v })}
                data-testid="sucursal-is-main"
              />
            </div>
            <div className="space-y-2">
              <Label>Estatus</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="sucursal-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => setSheetOpen(false)} data-testid="sucursal-cancel">Cancelar</Button>
              <Button type="submit" disabled={saving} className="btn-tenant" data-testid="sucursal-save">
                {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear sucursal"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar sucursal</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <b>{confirmDelete?.name}</b>. Esta acción no se puede deshacer y solo se permite si no tiene usuarios asignados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-sucursal">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
